import React, { FC, useState, useRef, useEffect } from 'react';
import { MARKET, MARKETS, IS_B2B, isBrandOV, isMarketES } from '../../constants/main';
import useTranslations from '@hooks/useTranslations';
import { CheckoutPreOrderProps } from '.';
import { Col, Grid, Row } from '../Grid';
import LoadingButton from '../LoadingButton';
import CheckoutMySummary from '@components/CheckoutMySummary';
import CheckoutTitle from '../CheckoutTitle';
import Checkbox from '../Checkbox';
import CheckoutTipsSidebar from '@components/CheckoutTipsSidebar';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../context/featureSwitchApp';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import { MySavedDealJourneyType } from '../../redux/user/user.duck.interface';
import useCustomRouter from '@hooks/useCustomRouter';
import { CashPaymentStatuses } from '../../constants/paymentStatuses';
import { scrollToRef } from '@utils/scroll.utils';
import { useJourneyType } from '@hooks/useJourneyType';
import LoaderOverlay from '@components/LoaderOverlay';
import CheckoutAssistedFinance from '@components/CheckoutAssistedFinance';
import { FINANCE_JOURNEY } from '../../types/journey';
import { useUserDuck } from '@hooks/useUserDuck';

const TIPS_SIDEBAR_ITEMS = [
    {
        ...(!IS_B2B && { title: 'checkout.preOrder.tips.item1.title' }),
        text: 'checkout.preOrder.tips.item1',
    },
    {
        ...(!IS_B2B && { title: 'checkout.preOrder.tips.item2.title' }),
        text: 'checkout.preOrder.tips.item2',
    },
    {
        ...(!IS_B2B && { title: 'checkout.preOrder.tips.item3.title' }),
        text: 'checkout.preOrder.tips.item3',
    },
];

const CheckoutPreOrderTemplate: FC<CheckoutPreOrderProps> = ({
    className,
    handleRedirect,
    showCatalanSwitch = false,
    catalanFirstSelected = false,
    handleToggleCatalanFirst = () => null,
    catalanSecondSelected = false,
    handleToggleCatalanSecond = () => null,
    catalanThirdSelected = false,
    handleToggleCatalanThird = () => null,
}) => {
    const { t, hasValue } = useTranslations();
    const { currentDeal } = useCarDetailsDuck();
    const { isUserLoggedInAsDealer } = useUserDuck();
    const [mounted, setMounted] = useState(false);
    const submitButtonRef = useRef(null);
    const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();
    const router = useCustomRouter();
    const { isFinance } = useJourneyType();
    const isProformaConsentEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_PROFORMA_CONSENT);
    const showAdditionalProformaLegalConsents =
        isFinance && useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_ADDITIONAL_PROFORMA_LEGAL_CONSENTS);
    const [consentAccepted, setConsentAccepted] = useState(!isProformaConsentEnabled);
    const [additional1ConsentAccepted, setAdditional1ConsentAccepted] = useState(!showAdditionalProformaLegalConsents);
    const [additional2ConsentAccepted, setAdditional2ConsentAccepted] = useState(!showAdditionalProformaLegalConsents);
    const [additional3ConsentAccepted, setAdditional3ConsentAccepted] = useState(false);
    const [additional4ConsentAccepted, setAdditional4ConsentAccepted] = useState(false);
    const handleToggleConsent = () => setConsentAccepted(!consentAccepted);
    const handleAdditional1ToggleConsent = () => setAdditional1ConsentAccepted(!additional1ConsentAccepted);
    const handleAdditional2ToggleConsent = () => setAdditional2ConsentAccepted(!additional2ConsentAccepted);
    const handleAdditional3ToggleConsent = () => setAdditional3ConsentAccepted(!additional3ConsentAccepted);
    const handleAdditional4ToggleConsent = () => setAdditional4ConsentAccepted(!additional4ConsentAccepted);

    useEffect(() => {
        setMounted(true);
    }, []);

    return (
        <div className={className}>
            {mounted ? (
                <>
                    <CheckoutTitle className="title" journeyType={currentDeal?.journeyType} />
                    <Grid>
                        <Row>
                            <Col className="col" xs={12} md={12} lg={9}>
                                {currentDeal.journeyType === FINANCE_JOURNEY ? (
                                    <CheckoutAssistedFinance />
                                ) : (
                                    <div className="subTitle">
                                        {hasValue(
                                            `checkout.preOrder.subTitle.paragraph1.${
                                                currentDeal?.journeyType === MySavedDealJourneyType.BUY_ONLINE_CASH
                                                    ? 'cash'
                                                    : 'finance'
                                            }`
                                        ) && (
                                            <p
                                                dangerouslySetInnerHTML={{
                                                    __html: t(
                                                        `checkout.preOrder.subTitle.paragraph1.${
                                                            currentDeal?.journeyType ===
                                                            MySavedDealJourneyType.BUY_ONLINE_CASH
                                                                ? 'cash'
                                                                : 'finance'
                                                        }`
                                                    ),
                                                }}
                                            />
                                        )}
                                        {currentDeal?.journeyType !== MySavedDealJourneyType.BUY_ONLINE_CASH &&
                                            hasValue('checkout.preOrder.subTitle.paragraph2.finance') && (
                                                <p
                                                    dangerouslySetInnerHTML={{
                                                        __html: t('checkout.preOrder.subTitle.paragraph2.finance'),
                                                    }}
                                                />
                                            )}
                                    </div>
                                )}
                                {router?.query.status &&
                                    router?.query.status !== CashPaymentStatuses.accepted &&
                                    currentDeal?.journeyType === MySavedDealJourneyType.BUY_ONLINE_CASH && (
                                        <div
                                            className="cashPaymentRejectedWrapper"
                                            onClick={() => scrollToRef(submitButtonRef)}
                                        >
                                            <span className="cashPaymentRejectedWrapper_title">
                                                {t('checkout.cashPayment.unsuccessful.title')}
                                            </span>
                                            <button className="cashPaymentRejectedWrapper_button">
                                                {t('checkout.cashPayment.unsuccessful.button')}
                                            </button>
                                        </div>
                                    )}
                                <CheckoutMySummary
                                    displayPx={MARKET === MARKETS.FR}
                                    isPxOpen
                                    allowPxFinalization
                                    isSecondaryLayout
                                    isOpenedByDefault
                                    isCheckout
                                />
                                {isProformaConsentEnabled && (
                                    <Checkbox
                                        className={'consent-checkbox'}
                                        onChange={handleToggleConsent}
                                        checked={consentAccepted}
                                        name={'consent-checkbox'}
                                        dataTestId="TESTING_CONFIRM_CHECKBOX"
                                    >
                                        <span
                                            className="checkbox-content"
                                            dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                                t(
                                                    `checkout.preOrder.consent.label${
                                                        currentDeal?.journeyType ===
                                                        MySavedDealJourneyType.BUY_ONLINE_CASH
                                                            ? '.cash'
                                                            : ''
                                                    }${isUserLoggedInAsDealer ? '.offline' : ''}`
                                                )
                                            )}
                                        />
                                    </Checkbox>
                                )}
                                {showAdditionalProformaLegalConsents && (
                                    <Checkbox
                                        className={'consent-checkbox'}
                                        onChange={handleAdditional1ToggleConsent}
                                        checked={additional1ConsentAccepted}
                                        name={'additional-legal-consents1'}
                                    >
                                        <span
                                            className="checkbox-content"
                                            dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                                t(
                                                    `checkout.preOrder.additional1.consent.label${
                                                        currentDeal.fullProductConfiguration.stock
                                                            ? '.stock'
                                                            : '.configurable'
                                                    }`
                                                )
                                            )}
                                        />
                                    </Checkbox>
                                )}
                                {showAdditionalProformaLegalConsents && (
                                    <Checkbox
                                        className={'consent-checkbox'}
                                        onChange={handleAdditional2ToggleConsent}
                                        checked={additional2ConsentAccepted}
                                        name={'additional-legal-consents2'}
                                    >
                                        <span
                                            className="checkbox-content"
                                            dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                                t(
                                                    `checkout.preOrder.additional2.consent.label${
                                                        currentDeal.fullProductConfiguration.stock
                                                            ? '.stock'
                                                            : '.configurable'
                                                    }`
                                                )
                                            )}
                                        />
                                    </Checkbox>
                                )}
                                {isFinance && isBrandOV && isMarketES && (
                                    <Checkbox
                                        className="consent-checkbox"
                                        onChange={handleAdditional3ToggleConsent}
                                        checked={additional3ConsentAccepted}
                                        name={'additional-legal-consents3'}
                                    >
                                        <span
                                            className="checkbox-content"
                                            dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                                t(
                                                    `checkout.preOrder.additional3.consent.label${
                                                        currentDeal?.journeyType ===
                                                        MySavedDealJourneyType.BUY_ONLINE_CASH
                                                            ? '.cash'
                                                            : ''
                                                    }`
                                                )
                                            )}
                                        />
                                    </Checkbox>
                                )}
                                {isFinance && isBrandOV && isMarketES && (
                                    <Checkbox
                                        className="consent-checkbox"
                                        onChange={handleAdditional4ToggleConsent}
                                        checked={additional4ConsentAccepted}
                                        name={'additional-legal-consents4'}
                                    >
                                        <span
                                            className="checkbox-content"
                                            dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                                t(
                                                    `checkout.preOrder.additional4.consent.label${
                                                        currentDeal?.journeyType ===
                                                        MySavedDealJourneyType.BUY_ONLINE_CASH
                                                            ? '.cash'
                                                            : ''
                                                    }`
                                                )
                                            )}
                                        />
                                    </Checkbox>
                                )}
                                {showCatalanSwitch && (
                                    <Checkbox
                                        className="consent-checkbox"
                                        onChange={handleToggleCatalanFirst}
                                        checked={catalanFirstSelected}
                                        name="catalan-checkbox"
                                    >
                                        <span
                                            className="checkbox-content"
                                            dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                                t(
                                                    `checkout.preOrder.catalan.label${
                                                        currentDeal?.journeyType ===
                                                        MySavedDealJourneyType.BUY_ONLINE_CASH
                                                            ? '.cash'
                                                            : ''
                                                    }`
                                                )
                                            )}
                                        />
                                    </Checkbox>
                                )}
                                <div ref={submitButtonRef}>
                                    <LoadingButton
                                        disabled={
                                            !consentAccepted ||
                                            !additional1ConsentAccepted ||
                                            !additional2ConsentAccepted ||
                                            (isFinance && isMarketES && isBrandOV && !additional3ConsentAccepted) ||
                                            (isFinance && isMarketES && isBrandOV && !additional4ConsentAccepted)
                                        }
                                        className="submitButton"
                                        data-testid="TESTING_CONTINUE_BUTTON"
                                        primary
                                        isFullWidth={!isProformaConsentEnabled}
                                        onClick={handleRedirect}
                                    >
                                        {t(
                                            `checkout.preOrder.submitButton.${
                                                currentDeal?.journeyType === MySavedDealJourneyType.BUY_ONLINE_CASH
                                                    ? 'cash'
                                                    : 'finance'
                                            }`
                                        )}
                                    </LoadingButton>
                                </div>
                                {showAdditionalProformaLegalConsents && (
                                    <p
                                        className="consent-description"
                                        dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                            t(
                                                t(
                                                    `checkout.preOrder.additional.consent.description${
                                                        currentDeal.fullProductConfiguration.stock
                                                            ? '.stock'
                                                            : '.configurable'
                                                    }`
                                                )
                                            )
                                        )}
                                    />
                                )}
                            </Col>
                            <Col className="wrapper-cols" xs={12} sm={12} md={12} lg={3}>
                                <div className="checkoutPreOrderSidebar">
                                    <CheckoutTipsSidebar
                                        title={'checkout.preOrder.tips.header'}
                                        items={TIPS_SIDEBAR_ITEMS}
                                        branchDifferences={false}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Grid>
                </>
            ) : (
                <LoaderOverlay loading />
            )}
        </div>
    );
};

export default CheckoutPreOrderTemplate;
